import { Dungeon } from './dungeon/Dungeon';
import { CellType } from './types';
import { generateDungeon, createDebugDungeon, selectCeilingLightPositions } from './dungeon/generator';
import { Renderer } from './renderer/Renderer';
import { Player } from './player/Player';
import { InputManager } from './input/InputManager';
import { Minimap } from './ui/Minimap';
import { StarCounter } from './ui/StarCounter';
import { LevelCounter } from './ui/LevelCounter';
import { DebugOverlay } from './ui/DebugOverlay';
import { AudioManager } from './audio/AudioManager';
import { Star, placeStars, STAR_COLLECT_RADIUS } from './entities/Star';
import {
  SpeedBoost,
  placeSpeedBoost,
  SPEED_BOOST_COLLECT_RADIUS,
  SPEED_BOOST_DURATION,
  SPEED_BOOST_MULTIPLIER
} from './entities/SpeedBoost';
import {
  WebTrap,
  placeWebTrap,
  WEB_TRAP_TRIGGER_RADIUS,
  WEB_TRAP_SLOW_MULTIPLIER,
  WEB_TRAP_SLOW_DURATION
} from './entities/WebTrap';
import {
  TurnaroundTrap,
  placeTurnaroundTrap,
  TURNAROUND_TRIGGER_RADIUS
} from './entities/TurnaroundTrap';
import {
  Breadcrumbs,
  placeBreadcrumbs,
  BREADCRUMBS_COLLECT_RADIUS
} from './entities/Breadcrumbs';
import {
  Arrow,
  placeArrow,
  ARROW_COLLECT_RADIUS
} from './entities/Arrow';
import { GreenFlash } from './ui/GreenFlash';
import { DungeonConfig } from './types';
import { setGlobalSeed } from './utils/random';
import { checkAndSaveBestTime, getHighestPlayedLevel, getBestTime } from './utils/bestTimes';
import { LevelSelectScreen, LevelCompleteInfo } from './ui/LevelSelectScreen';
import { NameInputModal } from './ui/NameInputModal';
import {
  getGlobalBest,
  getAllGlobalBests,
  submitGlobalBest,
  isFirebaseConfigured,
  GlobalBestRecord,
} from './firebase/leaderboard';

const DUNGEON_CONFIG: DungeonConfig = {
  width: 21,
  height: 21,
  cellSize: 1
};

const STAR_COUNT = 10;
const BASE_SEED = 12345;  // Change this to get different level sequences

class Game {
  private renderer: Renderer;
  private player!: Player;
  private input: InputManager;
  private minimap!: Minimap;
  private starCounter: StarCounter;
  private levelCounter: LevelCounter;
  private levelSelectScreen: LevelSelectScreen;
  private nameInputModal: NameInputModal;
  private debugOverlay: DebugOverlay;
  private audio: AudioManager;
  private dungeon!: Dungeon;
  private stars: Star[] = [];
  private speedBoost: SpeedBoost | null = null;
  private webTrap: WebTrap | null = null;
  private turnaroundTrap: TurnaroundTrap | null = null;
  private breadcrumbs: Breadcrumbs | null = null;
  private arrow: Arrow | null = null;
  private greenFlash!: GreenFlash;
  private lastTime = 0;
  private canvas: HTMLCanvasElement;
  private debugMode = false;
  private levelComplete = false;
  private globalBests: Map<number, GlobalBestRecord> = new Map();

  constructor() {
    // Get canvas
    this.canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
    if (!this.canvas) {
      throw new Error('Canvas not found');
    }

    // Set initial canvas size
    this.resizeCanvas();
    window.addEventListener('resize', () => this.resizeCanvas());

    // Initialize renderer
    this.renderer = new Renderer(this.canvas);

    // Initialize input
    this.input = new InputManager(this.canvas);

    // Initialize star counter UI
    this.starCounter = new StarCounter();

    // Initialize level counter UI
    this.levelCounter = new LevelCounter();

    // Initialize audio
    this.audio = new AudioManager();

    // Play short-circuit sound when ceiling lights flicker off
    this.renderer.setOnCeilingLightOff((lightX, lightZ) => {
      if (this.player) {
        const pos = this.player.getPosition();
        this.audio.playShortCircuitSound(lightX, lightZ, pos.x, pos.z, this.player.camera.yaw);
      }
    });

    // Initialize debug overlay (hidden by default)
    this.debugOverlay = new DebugOverlay();
    this.debugOverlay.hide();

    // Initialize level select screen (used for both initial selection and level complete)
    this.levelSelectScreen = new LevelSelectScreen();

    // Initialize name input modal for world records
    this.nameInputModal = new NameInputModal();

    // Initialize green flash overlay for turnaround trap
    this.greenFlash = new GreenFlash();

    // Resume audio context on first user interaction (required by browsers)
    const resumeAudio = () => this.audio.resume();
    this.canvas.addEventListener('click', resumeAudio, { once: true });
    document.addEventListener('keydown', resumeAudio, { once: true });

    // Check if player has previous progress
    const highestLevel = getHighestPlayedLevel();
    if (highestLevel > 0) {
      // Fetch global bests then show level select
      this.fetchGlobalBestsAndShowLevelSelect(highestLevel + 1);
    } else {
      // First time playing - start at level 1
      this.levelCounter.setBestTime(null);
      this.generateNewMaze();
    }
  }

  private async fetchGlobalBestsAndShowLevelSelect(
    maxLevel: number,
    completionInfo?: LevelCompleteInfo
  ): Promise<void> {
    if (isFirebaseConfigured()) {
      this.globalBests = await getAllGlobalBests();
    }

    this.levelSelectScreen.show(
      maxLevel,
      (level) => this.startAtLevel(level),
      completionInfo,
      this.globalBests
    );
  }

  private startAtLevel(level: number): void {
    this.levelComplete = false;
    this.levelCounter.setLevel(level);
    this.levelCounter.setBestTime(getBestTime(level));
    this.starCounter.reset();
    this.audio.resume();  // Resume audio on user interaction
    this.generateNewMaze();
  }

  private generateDebugRoom(): void {
    // Set consistent seed for debug room
    setGlobalSeed(BASE_SEED);

    // Generate a simple debug room
    this.dungeon = createDebugDungeon(DUNGEON_CONFIG);

    // Setup dungeon geometry
    this.renderer.setupDungeon(this.dungeon);

    // Place ceiling lights in debug room: grid + near walls
    const ceilingLightCells = selectCeilingLightPositions(this.dungeon);
    const testCeilingLights = [];  // Manually add some near walls for testing
    // for (let x = 1; x < DUNGEON_CONFIG.width - 1; x += 2) {
    //   testCeilingLights.push({ x, z: 1 });
    //   testCeilingLights.push({ x, z: DUNGEON_CONFIG.height - 2 });
    // }
    // for (let z = 1; z < DUNGEON_CONFIG.height - 1; z += 2) {
    //   testCeilingLights.push({ x: 1, z });
    //   testCeilingLights.push({ x: DUNGEON_CONFIG.width - 2, z });
    // }
    // lights around the pillar at (13,6)
    testCeilingLights.push({ x: 14, z: 6 });
    testCeilingLights.push({ x: 12, z: 6 });
    testCeilingLights.push({ x: 13, z: 7 });
    testCeilingLights.push({ x: 13, z: 5 });

    const allCells = [...testCeilingLights, ...ceilingLightCells];
    // const allCells = [...testCeilingLights];
    const ceilingLightPositions = allCells.map(cell => this.dungeon.cellToWorld(cell.x, cell.z));
    this.renderer.setupCeilingLights(ceilingLightPositions);

    // Place a single star in the center
    const centerX = Math.floor(DUNGEON_CONFIG.width / 2);
    const centerZ = Math.floor(DUNGEON_CONFIG.height / 2);
    const worldPos = this.dungeon.cellToWorld(centerX, centerZ);

    this.stars = [{
      position: { x: worldPos.x, y: 0.5, z: worldPos.z },
      rotation: 0,
      collected: false
    }];
    this.renderer.setupStars(this.stars);
    this.audio.setupStarSounds(this.stars);

    // Place speed boost in debug room
    const boostPos = this.dungeon.cellToWorld(centerX + 3, centerZ);
    this.speedBoost = {
      position: { x: boostPos.x, y: 0.5, z: boostPos.z },
      rotation: 0,
      collected: false
    };
    this.renderer.setupSpeedBoost(this.speedBoost);

    // Place web trap in debug room
    const webTrapPos = this.dungeon.cellToWorld(centerX - 3, centerZ);
    this.webTrap = {
      position: { x: webTrapPos.x, y: 0, z: webTrapPos.z },
      cellX: centerX - 3,
      cellZ: centerZ,
      triggered: false
    };
    this.renderer.setupWebTrap(this.webTrap);

    // Place turnaround trap in debug room
    const turnaroundPos = this.dungeon.cellToWorld(centerX, centerZ + 3);
    this.turnaroundTrap = {
      position: { x: turnaroundPos.x, y: 0, z: turnaroundPos.z },
      cellX: centerX,
      cellZ: centerZ + 3,
      triggered: false
    };
    this.renderer.setupTurnaroundTrap();

    // Place breadcrumbs in debug room
    const breadcrumbsPos = this.dungeon.cellToWorld(centerX + 3, centerZ + 3);
    this.breadcrumbs = {
      position: { x: breadcrumbsPos.x, y: 0.5, z: breadcrumbsPos.z },
      rotation: 0,
      collected: false,
      footprints: new Map()
    };
    this.renderer.setupBreadcrumbs(this.breadcrumbs);

    // Place arrow in debug room
    const arrowPos = this.dungeon.cellToWorld(centerX - 3, centerZ + 3);
    this.arrow = {
      position: { x: arrowPos.x, y: 0.5, z: arrowPos.z },
      rotation: 0,
      collected: false
    };
    this.renderer.setupArrow(this.arrow);

    // Update star counter
    this.starCounter.setTotal(this.stars.length);

    // Start player in a corner
    const startPos = this.dungeon.cellToWorld(2, 2);
    const startYaw = -Math.PI / 4; // Face toward center

    // Initialize or reset player
    if (this.player) {
      this.player.setDungeon(this.dungeon);
      this.player.setPosition(startPos.x, startPos.z);
      this.player.setYaw(startYaw);
    } else {
      this.player = new Player(this.dungeon, startPos.x, startPos.z);
      this.player.setYaw(startYaw);
    }

    // Initialize or reset minimap
    if (this.minimap) {
      this.minimap.destroy();
    }
    this.minimap = new Minimap(this.dungeon);
  }

  private generateNewMaze(): void {
    // Set seed based on level number for consistent levels
    const level = this.levelCounter.getLevel();
    setGlobalSeed(BASE_SEED + level);

    // Generate dungeon
    this.dungeon = generateDungeon(DUNGEON_CONFIG);

    // Setup dungeon geometry
    this.renderer.setupDungeon(this.dungeon);

    // Place ceiling lights on ~10% of open cells
    const ceilingLightCells = selectCeilingLightPositions(this.dungeon);
    const ceilingLightPositions = ceilingLightCells.map(cell => this.dungeon.cellToWorld(cell.x, cell.z));
    this.renderer.setupCeilingLights(ceilingLightPositions);

    // Find starting position first (needed to exclude from star placement)
    const startCell = this.dungeon.findOpenCell();
    if (!startCell) {
      throw new Error('No open cell found in dungeon');
    }

    // Place stars throughout the dungeon, excluding area near player start
    this.stars = placeStars(this.dungeon, STAR_COUNT, startCell, 4);
    this.renderer.setupStars(this.stars);
    this.audio.setupStarSounds(this.stars);

    // Place speed boost power-up
    this.speedBoost = placeSpeedBoost(this.dungeon, startCell, 5);
    this.renderer.setupSpeedBoost(this.speedBoost);

    // Build occupied cells set to avoid overlapping entity placements
    const occupiedCells = new Set<string>();
    // Add star cells
    for (const star of this.stars) {
      const cx = Math.floor(star.position.x / this.dungeon.cellSize);
      const cz = Math.floor(star.position.z / this.dungeon.cellSize);
      occupiedCells.add(`${cx},${cz}`);
    }
    // Add speed boost cell
    if (this.speedBoost) {
      const cx = Math.floor(this.speedBoost.position.x / this.dungeon.cellSize);
      const cz = Math.floor(this.speedBoost.position.z / this.dungeon.cellSize);
      occupiedCells.add(`${cx},${cz}`);
    }

    // Place web trap
    this.webTrap = placeWebTrap(this.dungeon, startCell, 4, occupiedCells);
    this.renderer.setupWebTrap(this.webTrap);
    if (this.webTrap) {
      occupiedCells.add(`${this.webTrap.cellX},${this.webTrap.cellZ}`);
    }

    // Place turnaround trap
    this.turnaroundTrap = placeTurnaroundTrap(this.dungeon, startCell, 4, occupiedCells);
    this.renderer.setupTurnaroundTrap();
    if (this.turnaroundTrap) {
      occupiedCells.add(`${this.turnaroundTrap.cellX},${this.turnaroundTrap.cellZ}`);
    }

    // Place breadcrumbs power-up
    this.breadcrumbs = placeBreadcrumbs(this.dungeon, startCell, 4, occupiedCells);
    this.renderer.setupBreadcrumbs(this.breadcrumbs);
    if (this.breadcrumbs) {
      const cx = Math.floor(this.breadcrumbs.position.x / this.dungeon.cellSize);
      const cz = Math.floor(this.breadcrumbs.position.z / this.dungeon.cellSize);
      occupiedCells.add(`${cx},${cz}`);
    }

    // Place arrow power-up
    this.arrow = placeArrow(this.dungeon, startCell, 4, occupiedCells);
    this.renderer.setupArrow(this.arrow);

    // Update star counter
    this.starCounter.setTotal(this.stars.length);

    // Get world position and initial facing direction
    const startPos = this.dungeon.cellToWorld(startCell.x, startCell.z);
    const startYaw = this.dungeon.findOpenDirection(startCell.x, startCell.z);

    // Initialize or reset player
    if (this.player) {
      this.player.setDungeon(this.dungeon);
      this.player.setPosition(startPos.x, startPos.z);
      this.player.setYaw(startYaw);
    } else {
      this.player = new Player(this.dungeon, startPos.x, startPos.z);
      this.player.setYaw(startYaw);
    }

    // Initialize or reset minimap
    if (this.minimap) {
      this.minimap.destroy();
    }
    this.minimap = new Minimap(this.dungeon);

    // Reveal entire map if player has already completed this level
    if (getBestTime(level) !== null) {
      this.minimap.revealAll();
    }
  }

  private resizeCanvas(): void {
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
    if (this.renderer) {
      this.renderer.resize(this.canvas.width, this.canvas.height);
    }
  }

  async start(): Promise<void> {
    await this.renderer.waitForTextures();

    const loadingScreen = document.getElementById('loading-screen');
    if (loadingScreen) {
      loadingScreen.classList.add('fade-out');
      loadingScreen.addEventListener('transitionend', () => loadingScreen.remove());
    }

    this.lastTime = performance.now();
    requestAnimationFrame((time) => this.gameLoop(time));
  }

  private gameLoop(currentTime: number): void {
    const deltaTime = (currentTime - this.lastTime) / 1000;
    this.lastTime = currentTime;

    this.update(deltaTime);
    this.render();

    requestAnimationFrame((time) => this.gameLoop(time));
  }

  private update(deltaTime: number): void {
    // Skip updates while level select screen or name input modal is showing, or player not loaded
    if (this.levelSelectScreen.isVisible() || this.nameInputModal.isVisible() || !this.player) {
      return;
    }

    const inputState = this.input.getState();

    // Check for debug mode toggle (Q key)
    if (this.input.wasKeyJustPressed('KeyQ')) {
      this.debugMode = !this.debugMode;
      if (this.debugMode) {
        this.debugOverlay.show();
        this.generateDebugRoom();
      } else {
        this.debugOverlay.hide();
        this.generateNewMaze();
      }
    }

    // Toggle player light (L key)
    if (this.input.wasKeyJustPressed('KeyL')) {
      this.renderer.togglePlayerLight();
    }

    // Toggle cell in front of player (T key, debug mode only)
    if (this.debugMode && this.input.wasKeyJustPressed('KeyT')) {
      const pos = this.player.getPosition();
      const fwd = this.player.camera.getForward();
      const targetX = pos.x + fwd.x;
      const targetZ = pos.z + fwd.z;
      const cell = this.dungeon.worldToCell(targetX, targetZ);
      const current = this.dungeon.getCell(cell.x, cell.z);
      if (current) {
        const newType = current.type === CellType.OPEN ? CellType.SOLID : CellType.OPEN;
        this.dungeon.setCell(cell.x, cell.z, newType);
        this.renderer.setupDungeon(this.dungeon);
      }
    }

    this.input.clearJustPressed();

    // Only allow movement when pointer is captured
    if (this.input.isLocked()) {
      // Update player rotation from mouse
      this.player.rotate(inputState.mouseDeltaX, inputState.mouseDeltaY);

      // Update player position from keyboard
      this.player.move(inputState.forward, inputState.right, deltaTime);
    }

    // Update level timer (only runs when pointer is captured)
    this.levelCounter.update(this.input.isLocked());

    // Update star ambient sounds based on player distance and heading
    const playerPos = this.player.getPosition();
    this.audio.updateStarSounds(playerPos.x, playerPos.z, this.player.camera.yaw);

    // Update debug overlay
    if (this.debugMode) {
      this.debugOverlay.update(playerPos, this.player.camera.yaw, this.stars, this.dungeon);
    }

    // Check for star collection
    this.checkStarCollisions();

    // Update breadcrumbs: record footprints as player moves
    if (this.breadcrumbs && this.breadcrumbs.collected) {
      const cellX = Math.floor(playerPos.x / this.dungeon.cellSize);
      const cellZ = Math.floor(playerPos.z / this.dungeon.cellSize);
      const key = `${cellX},${cellZ}`;
      this.breadcrumbs.footprints.set(key, this.player.camera.yaw);
    }

    // Update floating arrow: point toward nearest uncollected star
    if (this.arrow && this.arrow.collected) {
      let nearestStar: Star | null = null;
      let nearestDist = Infinity;

      for (const star of this.stars) {
        if (star.collected) continue;
        const dx = star.position.x - playerPos.x;
        const dz = star.position.z - playerPos.z;
        const dist = Math.sqrt(dx * dx + dz * dz);
        if (dist < nearestDist) {
          nearestDist = dist;
          nearestStar = star;
        }
      }

      if (nearestStar) {
        const dx = nearestStar.position.x - playerPos.x;
        const dz = nearestStar.position.z - playerPos.z;
        // Angle from player to star (yaw convention: 0 = -Z)
        const angle = Math.atan2(-dx, -dz);

        // Position arrow in view-space: forward along look direction, offset down
        const cam = this.player.camera;
        const look = cam.getLookDirection();
        const cosPitch = Math.cos(cam.pitch);
        const sinPitch = Math.sin(cam.pitch);
        const cosYaw = Math.cos(cam.yaw);
        const sinYaw = Math.sin(cam.yaw);
        // Camera up vector
        const upX = sinYaw * sinPitch;
        const upY = cosPitch;
        const upZ = cosYaw * sinPitch;

        const viewDist = 0.5;
        const downOffset = 0.22;
        this.renderer.updateFloatingArrow(
          {
            x: cam.position.x + look.x * viewDist - upX * downOffset,
            y: cam.position.y + look.y * viewDist - upY * downOffset,
            z: cam.position.z + look.z * viewDist - upZ * downOffset
          },
          angle
        );
      } else {
        this.renderer.clearFloatingArrow();
      }
    }

    // Check if all stars collected (skip in debug mode to allow re-testing)
    if (this.starCounter.isComplete() && !this.debugMode && !this.levelComplete) {
      this.levelComplete = true;
      this.handleLevelComplete();
    }
  }

  private async handleLevelComplete(): Promise<void> {
    const level = this.levelCounter.getLevel();
    const time = this.levelCounter.getElapsedTime();
    const isNewBest = checkAndSaveBestTime(level, time);

    let isNewWorldRecord = false;

    // Check if this beats the global record
    if (isFirebaseConfigured()) {
      const globalBest = await getGlobalBest(level);
      if (!globalBest || time < globalBest.time) {
        isNewWorldRecord = true;
        // Show name input modal
        const playerName = await this.nameInputModal.show();
        await submitGlobalBest(level, time, playerName);
        // Update local cache
        this.globalBests.set(level, { time, playerName });
      }
    }

    const completionInfo: LevelCompleteInfo = {
      level,
      timeMs: time,
      isNewBest,
      isNewWorldRecord,
    };
    const highestLevel = getHighestPlayedLevel();

    this.fetchGlobalBestsAndShowLevelSelect(
      highestLevel + 1,
      completionInfo
    );
  }

  private checkStarCollisions(): void {
    const playerPos = this.player.getPosition();

    for (const star of this.stars) {
      if (star.collected) continue;

      // Calculate distance to star (in XZ plane, ignore Y)
      const dx = playerPos.x - star.position.x;
      const dz = playerPos.z - star.position.z;
      const distance = Math.sqrt(dx * dx + dz * dz);

      if (distance < STAR_COLLECT_RADIUS) {
        star.collected = true;
        this.starCounter.increment();
        this.audio.stopStarSound(star.position.x, star.position.z);
        this.audio.playStarCollect();
      }
    }

    // Check speed boost collection
    if (this.speedBoost && !this.speedBoost.collected) {
      const dx = playerPos.x - this.speedBoost.position.x;
      const dz = playerPos.z - this.speedBoost.position.z;
      const distance = Math.sqrt(dx * dx + dz * dz);

      if (distance < SPEED_BOOST_COLLECT_RADIUS) {
        this.speedBoost.collected = true;
        this.player.applySpeedBoost(SPEED_BOOST_MULTIPLIER, SPEED_BOOST_DURATION);
        this.audio.playWindWhoosh();
      }
    }

    // Check web trap
    if (this.webTrap && !this.webTrap.triggered) {
      const dx = playerPos.x - this.webTrap.position.x;
      const dz = playerPos.z - this.webTrap.position.z;
      const distance = Math.sqrt(dx * dx + dz * dz);

      if (distance < WEB_TRAP_TRIGGER_RADIUS) {
        this.webTrap.triggered = true;
        this.player.applySlowDebuff(WEB_TRAP_SLOW_MULTIPLIER, WEB_TRAP_SLOW_DURATION);
        this.audio.playWebTrapSound();
      }
    }

    // Check turnaround trap
    if (this.turnaroundTrap && !this.turnaroundTrap.triggered) {
      const dx = playerPos.x - this.turnaroundTrap.position.x;
      const dz = playerPos.z - this.turnaroundTrap.position.z;
      const distance = Math.sqrt(dx * dx + dz * dz);

      if (distance < TURNAROUND_TRIGGER_RADIUS) {
        this.turnaroundTrap.triggered = true;
        this.player.setYaw(this.player.camera.yaw + Math.PI);
        this.greenFlash.flash();
      }
    }

    // Check breadcrumbs collection
    if (this.breadcrumbs && !this.breadcrumbs.collected) {
      const dx = playerPos.x - this.breadcrumbs.position.x;
      const dz = playerPos.z - this.breadcrumbs.position.z;
      const distance = Math.sqrt(dx * dx + dz * dz);

      if (distance < BREADCRUMBS_COLLECT_RADIUS) {
        this.breadcrumbs.collected = true;
        this.audio.playBreadcrumbsPickup();
      }
    }

    // Check arrow collection
    if (this.arrow && !this.arrow.collected) {
      const dx = playerPos.x - this.arrow.position.x;
      const dz = playerPos.z - this.arrow.position.z;
      const distance = Math.sqrt(dx * dx + dz * dz);

      if (distance < ARROW_COLLECT_RADIUS) {
        this.arrow.collected = true;
        this.audio.playArrowPickup();
      }
    }
  }

  private render(): void {
    // Skip rendering if dungeon/player not yet loaded or modal is showing
    if (!this.dungeon || !this.player) {
      return;
    }
    this.renderer.render(this.player.camera);
    const levelPreviouslyCompleted = getBestTime(this.levelCounter.getLevel()) !== null;
    this.minimap.render(
      this.player,
      this.stars,
      this.speedBoost,
      this.webTrap,
      this.turnaroundTrap,
      this.breadcrumbs,
      this.arrow,
      levelPreviouslyCompleted
    );
  }
}

// Start the game when the page loads
window.addEventListener('DOMContentLoaded', () => {
  try {
    const game = new Game();
    game.start();
  } catch (error) {
    console.error('Failed to start game:', error);
    const instructions = document.getElementById('instructions');
    if (instructions) {
      instructions.innerHTML = `Error: ${error instanceof Error ? error.message : 'Unknown error'}`;
    }
  }
});
